/**
 * Inline visualisation of the per-pool routing weights that drive
 * Envoy's Layer-1 (LocalityLbEndpoints) pool selection.
 *
 * Each row shows one pool's share of traffic. The autoscaler is the
 * sole writer — it spreads weight across priorities based on each
 * pool's SLO headroom and capacity, so a multi-pool split is the
 * normal steady state whenever the workload doesn't fit a single
 * pool. See `controller/controller/routing/locality_weights.py` for
 * the source of truth and `controller/controller/autoscaler/loop.py`
 * for the write side.
 *
 * The bar itself is read-only — operators don't directly edit weights;
 * they tune SLOs and capacity, and the autoscaler reflects the result
 * here.
 */
import type { Pool } from "@/api/types";
import { Pill } from "@/components/Pill";
import { humanShapeLabel, poolShapeSlug } from "@/workloads/tree-data";

export interface LocalityWeightRow {
  pool: Pool;
  /** 0 (closed) – 1000 (full traffic). */
  weight: number;
}

interface LocalityWeightBarProps {
  rows: LocalityWeightRow[];
}

const TONE_VAR = "var(--brand)";

export function LocalityWeightBar({ rows }: LocalityWeightBarProps) {
  if (rows.length === 0) {
    return (
      <div
        className="empty"
        style={{
          padding: 16,
          border: "1px dashed var(--edge-strong)",
          borderRadius: 8,
          textAlign: "center",
          fontSize: 12,
        }}
      >
        no pools — nothing to weight
      </div>
    );
  }

  const sorted = rows
    .slice()
    .sort((a, b) => a.pool.priority - b.pool.priority);
  const totalWeight = sorted.reduce((acc, r) => acc + r.weight, 0);

  return (
    <div className="locality-weights" style={{ display: "grid", gap: 6 }}>
      {sorted.map(({ pool, weight }) => {
        const sharePct =
          totalWeight > 0 ? Math.round((weight / totalWeight) * 100) : 0;
        return (
          <div
            key={pool.id}
            style={{
              display: "grid",
              gridTemplateColumns: "60px 1fr 80px 60px",
              gap: 8,
              alignItems: "center",
              fontSize: 12,
            }}
          >
            <Pill tone="info">P{pool.priority}</Pill>
            <div
              className="weight-bar"
              style={{
                position: "relative",
                height: 14,
                background: "var(--surface-2)",
                borderRadius: 4,
                overflow: "hidden",
              }}
              title={humanShapeLabel(poolShapeSlug(pool))}
            >
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: `${(weight / 1000) * 100}%`,
                  background: TONE_VAR,
                  opacity: weight === 0 ? 0.15 : 0.8,
                  transition: "width 250ms ease",
                }}
              />
              <span
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  paddingLeft: 6,
                  fontSize: 11,
                  color: "var(--ink)",
                  mixBlendMode: "difference",
                }}
              >
                {humanShapeLabel(poolShapeSlug(pool))}
              </span>
            </div>
            <span className="mono num muted" style={{ textAlign: "right" }}>
              w {weight}
            </span>
            <span className="mono num muted" style={{ textAlign: "right" }}>
              {sharePct}%
            </span>
          </div>
        );
      })}
    </div>
  );
}
